import { cxScoped, CxGlobals, cxRecent } from '@proc7ts/context-values';

/**
 * Naming schema is responsible for applying namespace aliases to simple names. E.g. by appending alias as prefix or
 * suffix of the name.
 */
class Naming {
    /**
     * Converts the given qualified `name` into simple one accordingly to this naming schema.
     *
     * @param name Qualified name to convert.
     * @param nsAlias Namespace aliaser to use.
     */
    name(name, nsAlias) {
        if (typeof name === 'string') {
            return name;
        }
        const [local, ns] = name;
        return ns.name(nsAlias(ns), local, this);
    }
}

class DefaultNaming extends Naming {
    applyAlias(name, alias) {
        return `${alias}-${name}`;
    }
}
/**
 * Default naming schema.
 *
 * Prefixes a name with namespace alias separating them by dash.
 *
 * The result looks like `<alias>-<name>`.
 */
const default__naming = ( /*#__PURE__*/new DefaultNaming());
/**
 * HTML elements naming schema.
 *
 * Prefixes a name with namespace alias separating them by dash.
 *
 * The result looks like `<alias>-<name>`.
 */
const html__naming = ( /*#__PURE__*/new DefaultNaming());
class XmlNaming extends Naming {
    applyAlias(name, alias) {
        return `${alias}:${name}`;
    }
}
/**
 * XML elements naming schema.
 *
 * Prefixes a name with namespace alias separating them by colon.
 *
 * The result looks like `<alias>:<name>`.
 */
const xml__naming = ( /*#__PURE__*/new XmlNaming());
/**
 * Element identifiers naming schema.
 *
 * Prefixes a name with namespace alias separating them by colon.
 *
 * The result looks like `<alias>:<name>`.
 */
const id__naming = ( /*#__PURE__*/new XmlNaming());
class CssNaming extends Naming {
    applyAlias(name, alias) {
        return `${name}@${alias}`;
    }
}
/**
 * CSS classes naming scheme.
 *
 * Appends namespace alias as a name suffix separated by `@` sign.
 *
 * The result looks like `<name>@<alias>`.
 */
const css__naming = ( /*#__PURE__*/new CssNaming());

/**
 * Namespace definition.
 *
 * Namespaces are identified by their URLs.
 */
class NamespaceDef {
    /**
     * Constructs new namespace definition.
     *
     * @param url - Unique namespace URL.
     * @param aliases - Preferred namespace aliases. It is expected that each alias is an ASCII letter followed by
     * any number of ASCII letters, digits, `-`, or `_` signs. Aliases starting with `xml` are reserved. Empty alias
     * is reserved for {@link DEFAULT__NS default namespace}.
     */
    constructor(url, ...aliases) {
        this.url = url;
        this.aliases = aliases;
    }
    /**
     * The most preferred namespace alias.
     *
     * By default this is the first preferred alias, or `ns` if there is no preferred aliases.
     */
    get alias() {
        return this.aliases[0] || 'ns';
    }
    /**
     * Converts a local `name` belonging to this namespace to simple one according to the given `naming` schema.
     *
     * Calls {@link Naming.applyAlias} by default.
     *
     * @param alias - Namespace alias to apply to the name.
     * @param name - A name to convert.
     * @param naming - Naming schema to use. {@link default__naming default naming schema} is used when omitted.
     *
     * @returns A simple name with this namespace alias applied.
     */
    name(alias, name, naming = default__naming) {
        return naming.applyAlias(name, alias, this);
    }
}

class DefaultNs extends NamespaceDef {
    constructor() {
        super('');
    }
    name(_alias, name) {
        return name;
    }
}
/**
 * Default namespace.
 *
 * This namespace is assumed for names without namespace specified.
 *
 * Its URL is empty. And it does not alter names, i.e. its {@link NamespaceDef.name} method returns the name as is.
 */
const DEFAULT__NS = ( /*#__PURE__*/new DefaultNs());

/**
 * Checks whether the given `value` is a name+namespace tuple.
 *
 * @param value - A value to check.
 *
 * @returns `true` if the given `value` is an array consisting of exactly two elements, where the first element is a
 * non-empty string, and the second element is an instance of {@link NamespaceDef}. Or `false` otherwise.
 */
function isNameAndNamespace(value) {
    return Array.isArray(value)
        && value.length === 2
        && typeof value[0] === 'string'
        && isNamespaceDef(value[1]);
}
function isNamespaceDef(value) {
    if (value instanceof NamespaceDef) {
        return true;
    }
    return typeof value === 'object'
        && typeof value.url === 'string'
        && typeof value.alias === 'string'
        && Array.isArray(value.aliases)
        && typeof value.name === 'function';
}
/**
 * Checks whether the given `value` is a qualified name.
 *
 * @param value - A value to check.
 *
 * @returns `true` if the given `value` is a string, or an array consisting of exactly two elements, where the first
 * element is a non-empty string, and the second element is an instance of {@link NamespaceDef}. `false` otherwise.
 */
function isQualifiedName(value) {
    return typeof value === 'string' || isNameAndNamespace(value);
}
/**
 * Detects a namespace of the given qualified `name`
 *
 * @param name - Qualified name to detect a namespace of.
 *
 * @returns A namespace if the given `name` has it, or {@link DEFAULT__NS default namespace} otherwise.
 */
function namespaceOf(name) {
    return typeof name !== 'string' ? name[1] : DEFAULT__NS;
}
/**
 * Converts the given qualified `name` to local name and namespace tuple.
 *
 * @param name - Qualified name to convert.
 *
 * @returns The `name` itself if it has a namespace, or a tuple consisting of `name` and
 * {@link DEFAULT__NS default namespace} otherwise.
 */
function nameAndNamespace(name) {
    return typeof name !== 'string' ? name : [name, DEFAULT__NS];
}
/**
 * Checks whether two qualified names are equal to each other.
 *
 * @param first - First qualified name to compare.
 * @param second - Second qualified name to compare.
 *
 * @returns `true` if both names are equal, or `false` otherwise.
 */
function namesEqual(first, second) {
    if (typeof first === 'string') {
        return typeof second === 'string' ? first === second : !second[1].url && second[0] === first;
    }
    const [firstName, { url: firstUrl }] = first;
    if (typeof second === 'string') {
        return !firstUrl && firstName === second;
    }
    return firstName === second[0] && firstUrl === second[1].url;
}
/**
 * Compares two qualified names.
 *
 * Names in default namespace considered less than other names. Namespaces are compared by their URLs.
 *
 * @param first - First qualified name to compare.
 * @param second - Second qualified name to compare.
 *
 * @returns `-1` if the `first` name is less than the `second` one, `0` if they are equal, or `1` if the `first` name
 * is greater than the `second` one.
 */
function compareNames(first, second) {
    if (typeof first === 'string') {
        if (typeof second === 'string') {
            return compareStrings(first, second);
        }
        if (!second[1].url) {
            return compareStrings(first, second[0]);
        }
        return -1;
    }
    const [firstName, { url: firstUrl }] = first;
    if (typeof second === 'string') {
        if (!firstUrl) {
            return compareStrings(firstName, second);
        }
        return 1;
    }
    return compareStrings(firstUrl, second[1].url) || compareStrings(firstName, second[0]);
}
function compareStrings(first, second) {
    return first < second ? -1 : first > second ? 1 : 0;
}

/**
 * Global context entry containing namespace aliaser instance.
 *
 * Tracks the most recently provided namespace aliaser. Creates new namespace aliaser instance by default.
 */
const NamespaceAliaser = {
    perContext: ( /*#__PURE__*/cxScoped(CxGlobals, ( /*#__PURE__*/cxRecent({
        create: (recent, _) => recent,
        byDefault: _ => newNamespaceAliaser(),
        assign: ({ get, to }, _) => {
            const nsAlias = ns => get()(ns);
            return receiver => to((_, by) => receiver(nsAlias, by));
        },
    })))),
    toString: () => '[NamespaceAliaser]',
};
/**
 * Creates a namespace aliaser.
 *
 * The returned function tries to find a registered alias for the given namespace. If not found then tries to use one
 * of its preferred aliases. If all of them are reserved already for another namespaces, generates a new unique alias.
 *
 * @returns New instance of namespace aliaser.
 */
function newNamespaceAliaser() {
    const aliasesByNs = new Map();
    const nsNumPerAlias = new Map();
    return function nsAlias(ns) {
        const found = aliasesByNs.get(ns.url);
        if (found) {
            return found;
        }
        const mostPreferred = ns.alias;
        let nsNumRegistered = 0;
        for (const preferred of [mostPreferred, ...ns.aliases]) {
            const ids = nsNumPerAlias.get(preferred);
            if (!ids) {
                aliasesByNs.set(ns.url, preferred);
                nsNumPerAlias.set(preferred, 1);
                return preferred;
            }
            if (!nsNumRegistered) {
                // Use the first one
                nsNumRegistered = ids;
            }
        }
        const generated = `${mostPreferred}${++nsNumRegistered}`;
        aliasesByNs.set(ns.url, generated);
        nsNumPerAlias.set(mostPreferred, nsNumRegistered);
        return generated;
    };
}

/**
 * [MathML](https://www.w3.org/Math/) namespace definition.
 */
const MathML__NS = ( /*#__PURE__*/new NamespaceDef('http://www.w3.org/1998/Math/MathML', 'math', 'mathml'));
/**
 * [SVG](https://www.w3.org/Graphics/SVG/) namespace definition.
 */
const SVG__NS = ( /*#__PURE__*/new NamespaceDef('http://www.w3.org/2000/svg', 'svg'));
/**
 * [XHTML](https://www.w3.org/TR/xhtml1/) namespace definition.
 */
const XHTML__NS = ( /*#__PURE__*/new NamespaceDef('http://www.w3.org/1999/xhtml', 'xhtml'));
/**
 * [XML](https://www.w3.org/XML/1998/namespace) namespace definition.
 *
 * It is always bound to `xml` alias.
 */
const XML__NS = ( /*#__PURE__*/new NamespaceDef('http://www.w3.org/XML/1998/namespace', 'xml'));
/**
 * [XML Namespaces](https://www.w3.org/TR/xml-names/#ns-decl) namespace definition.
 *
 * It is always bound to `xmlns` alias and should never be declared.
 */
const XMLNS__NS = ( /*#__PURE__*/new NamespaceDef('http://www.w3.org/2000/xmlns/', 'xmlns'));
/**
 * [XSL Transformations](https://www.w3.org/TR/1999/REC-xslt-19991116#xslt-namespace) namespace definition.
 */
const XSLT__NS = ( /*#__PURE__*/new NamespaceDef('http://www.w3.org/1999/XSL/Transform', 'xsl'));

export { DEFAULT__NS, MathML__NS, NamespaceAliaser, NamespaceDef, Naming, SVG__NS, XHTML__NS, XMLNS__NS, XML__NS, XSLT__NS, compareNames, css__naming, default__naming, html__naming, id__naming, isNameAndNamespace, isQualifiedName, nameAndNamespace, namesEqual, namespaceOf, newNamespaceAliaser, xml__naming };
//# sourceMappingURL=namespace-aliaser.js.map
